/**
 * @module PlatformService
 * @purpose API client functions for platform status and demo-mode administration.
 * @depends apiClient, ApiError (services/api.ts), platform types (types/platform.ts)
 * @dependedOnBy hooks/components consuming platform status and demo-mode toggling
 *
 * @rules
 * - Performance: stateless service functions; state lives in hooks
 * - State: stateless
 * - Errors: ApiError propagates from apiClient — no try-catch swallowing. Use
 *   `getDemoModeErrorCode` to branch on the backend error code without importing
 *   ApiError internals into every caller.
 */

import { ApiError, apiClient } from './api'
import type {
  DemoLlmProvider,
  DemoModeErrorCode,
  ListPlatformEventsResponse,
  PlatformStatus,
  SetDemoLlmProviderRequest,
  ToggleDemoModeRequest,
} from '../types/platform'

/**
 * GET /api/platform/status — works both unauthenticated and authenticated.
 *
 * @behaviour Does NOT pass `skipAuth`: the bearer token (when present) must be
 *            attached so the backend can return the authenticated response
 *            shape (`enabledBy`, `enabledAt`, `llmProvider`, `environment`,
 *            `capabilities`). Callers with no stored token still succeed —
 *            `buildRequestHeaders` simply omits the header when there is no
 *            token, and this endpoint never 401s.
 * @errors Propagates ApiError from apiClient.
 */
export async function getPlatformStatus(): Promise<PlatformStatus> {
  return apiClient.get<PlatformStatus>('/platform/status')
}

/**
 * PUT /api/platform/demo-mode — enable or disable demo mode.
 *
 * @behaviour Requires the platform PIN and the caller's current password.
 * @errors Propagates ApiError (403 DEMO_MODE_UNSUPPORTED_ENVIRONMENT | 403
 *         DEMO_MODE_DENIED | 409 DEMO_MODE_PIN_NOT_CONFIGURED | 429
 *         DEMO_MODE_RATE_LIMITED | 400 VALIDATION_ERROR).
 */
export async function toggleDemoMode(request: ToggleDemoModeRequest): Promise<PlatformStatus> {
  return apiClient.put<PlatformStatus>('/platform/demo-mode', request)
}

/**
 * PUT /api/platform/demo-mode/llm-provider — switch between the primary and
 * alternate LLM provider while demo mode is enabled.
 *
 * @behaviour Only meaningful while demo mode is enabled for this platform.
 * @errors Propagates ApiError (403 DEMO_MODE_REQUIRED | 422
 *         ALTERNATE_PROFILE_NOT_CONFIGURED).
 */
export async function setDemoLlmProvider(provider: DemoLlmProvider): Promise<PlatformStatus> {
  const request: SetDemoLlmProviderRequest = { provider }
  return apiClient.put<PlatformStatus>('/platform/demo-mode/llm-provider', request)
}

export interface ListPlatformEventsParams {
  limit?: number
  offset?: number
}

/**
 * GET /api/platform/events — recent platform-level audit events (demo-mode
 * toggles, provider switches, and similar admin actions).
 *
 * @behaviour `limit` defaults to 50 and is clamped to 200 server-side.
 * @errors Propagates ApiError from apiClient.
 */
export async function listPlatformEvents(
  params?: ListPlatformEventsParams
): Promise<ListPlatformEventsResponse> {
  const query = new URLSearchParams()
  if (params?.limit !== undefined) query.set('limit', String(params.limit))
  if (params?.offset !== undefined) query.set('offset', String(params.offset))
  const suffix = query.toString()
  return apiClient.get<ListPlatformEventsResponse>(
    suffix ? `/platform/events?${suffix}` : '/platform/events'
  )
}

/**
 * Extract the backend demo-mode error code from a caught error without
 * requiring callers to import `ApiError` internals. Mirrors the code-comparison
 * pattern used by `use-upload-receipt-modal.ts` (`err.body?.error`).
 *
 * @behaviour Returns `undefined` when `err` is not an `ApiError` or carries no
 *            body. The cast is intentionally loose: the backend is the registry
 *            of record, so an unlisted code passes through rather than being
 *            narrowed away — callers' switch/default paths must stay total.
 * @errors Never throws.
 */
export function getDemoModeErrorCode(err: unknown): DemoModeErrorCode | undefined {
  if (!(err instanceof ApiError)) return undefined
  return err.body?.error as DemoModeErrorCode | undefined
}
